import { type Action } from '@reduxjs/toolkit';
import { call, type CallEffect, put, type PutEffect } from 'redux-saga/effects';
import { type Socket } from 'socket.io-client';

import { requestFailed, requestTimeoutReport } from './request-failed/reducer';
import { ERequestFailedReason } from './request-failed/types';

export interface IAction {
  type: string;
}

interface ISendOptions {
  timeoutMs?: number;
}

export const REQUEST_TIMEOUT_MS = 10000;
export const PAYMENT_REQUEST_TIMEOUT_MS = 30000;

const sendRaw = <ACTION extends IAction, RESPONSE>(
  socket: Socket,
  action: ACTION,
  { timeoutMs = REQUEST_TIMEOUT_MS }: ISendOptions = {},
): Promise<void | RESPONSE | ReturnType<typeof requestFailed>> =>
  new Promise((resolve): void => {
    socket
      .timeout(timeoutMs)
      .emit(
        action.type,
        action,
        (error: Error | null, response: RESPONSE): void => {
          if (error) {
            socket.emit(
              requestTimeoutReport.type,
              requestTimeoutReport({ requestType: action.type, timeoutMs }),
            );

            resolve(
              requestFailed({
                requestType: action.type,
                reason: ERequestFailedReason.Timeout,
              }),
            );

            return;
          }

          resolve(response);
        },
      );
  });

const emit = <ACTION extends IAction>(socket: Socket, action: ACTION): void => {
  socket.emit(action.type, action);
};

function* send<ACTION extends IAction>(
  socket: Socket,
  action: ACTION,
): Generator<CallEffect | PutEffect, void, Action> {
  const response: Action = yield call(sendRaw, socket, action);

  yield put(response);
}

function* sendPayment<ACTION extends IAction>(
  socket: Socket,
  action: ACTION,
): Generator<CallEffect | PutEffect, void, Action> {
  const response: Action = yield call(sendRaw, socket, action, {
    timeoutMs: PAYMENT_REQUEST_TIMEOUT_MS,
  });

  yield put(response);
}

function* sendWithoutResponse<ACTION extends IAction>(
  socket: Socket,
  action: ACTION,
): Generator<CallEffect, void, void> {
  yield call(emit, socket, action);
}

export { emit, send, sendPayment, sendRaw, sendWithoutResponse };
